import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Sparkles, Users, Heart, TrendingUp, MapPin, ArrowRight } from 'lucide-react';
import MyButton from '../../../components/common/Button/MyButton';
import '../../../assets/styles/customer/layout/AboutHeroSection.css';

const AboutHeroSection = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const [stats, setStats] = useState({
    distributors: 0,
    customers: 0,
    deliveries: 0,
    cities: 0
  });

  const finalStats = {
    distributors: 500,
    customers: 25000,
    deliveries: 1000000,
    cities: 50
  };

  // Intersection Observer for scroll detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  // Counter Animation
  useEffect(() => {
    if (!hasAnimated) return;

    const duration = 2500;
    const steps = 60;

    const animateCounter = (key, target) => {
      const increment = target / steps;
      let current = 0;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        current = increment * step;
        
        if (step >= steps) {
          setStats(prev => ({ ...prev, [key]: target }));
          clearInterval(timer);
        } else {
          setStats(prev => ({ ...prev, [key]: current }));
        }
      }, duration / steps);
    };

    animateCounter('distributors', finalStats.distributors);
    animateCounter('customers', finalStats.customers);
    animateCounter('deliveries', finalStats.deliveries);
    animateCounter('cities', finalStats.cities);
  }, [hasAnimated]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + 'K';
    }
    return Math.floor(num).toLocaleString();
  };

  return (
    <section className="about-hero-section" ref={sectionRef}>
      
      {/* Floating Milk Bottles */}
      <div className="about-floating-bottles">
        <div className="about-bottle bottle-1">🥛</div>
        <div className="about-bottle bottle-2">🥛</div>
        <div className="about-bottle bottle-3">🥛</div>
        <div className="about-bottle bottle-4">🥛</div>
      </div>

      {/* Floating Circles */}
      <div className="about-floating-circles">
        <div className="about-circle circle-1"></div>
        <div className="about-circle circle-2"></div>
        <div className="about-circle circle-3"></div>
      </div>

      {/* Wavy Background */}
      <div className="about-wave-bg">
        <svg className="about-wave" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path 
            fill="rgba(62, 150, 231, 0.05)" 
            d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,154.7C672,149,768,171,864,181.3C960,192,1056,192,1152,181.3C1248,171,1344,149,1392,138.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </div>

      {/* Decorative Icons */}
      <div className="about-decorative-icons">
        <div className="about-icon-float icon-1">
          <Sparkles size={28} strokeWidth={1.5} />
        </div>
        <div className="about-icon-float icon-2">
          <Heart size={26} strokeWidth={1.5} />
        </div>
        <div className="about-icon-float icon-3">
          <TrendingUp size={30} strokeWidth={1.5} />
        </div>
      </div>

      <Container>
        <Row className="align-items-center">
          
          {/* Left Content */}
          <Col lg={6} md={12}>
            <div className="about-hero-content">
              
              {/* Badge */}
              <div className="about-hero-badge">
                <Sparkles size={16} />
                <span>About MilkFlow</span>
              </div>

              {/* Title */}
              <h1 className="about-hero-title">
                Transforming Dairy Delivery, <span className="about-hero-highlight">One Drop at a Time</span>
              </h1>

              {/* Description */}
              <p className="about-hero-description">
                We're on a mission to modernize Pakistan's dairy supply chain by bringing 
                technology to local milk distributors and their customers.
              </p>

              {/* CTA Buttons */}
              <div className="about-hero-buttons">
                <MyButton variant="primary" className="join-btn">
                  Join Our Mission
                </MyButton>
                <button className="touch-btn">
                  Get in Touch
                  <ArrowRight size={18} className="ms-2" />
                </button>
              </div>

            </div>
          </Col>

          {/* Right Stats Card */}
          <Col lg={6} md={12}>
            <div className="about-stats-card">
              <Row className="g-4">
                
                {/* Distributors Stat */}
                <Col xs={6}>
                  <div className="about-stat-box">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(62, 150, 231, 0.1)' }}>
                      <Users size={28} color="#3e96e7" strokeWidth={2} />
                    </div>
                    <div className="stat-value">{formatNumber(stats.distributors)}+</div>
                    <div className="stat-label">Active Distributors</div>
                  </div>
                </Col>

                {/* Customers Stat */}
                <Col xs={6}>
                  <div className="about-stat-box">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(76, 175, 80, 0.1)' }}>
                      <Heart size={28} color="#4caf50" strokeWidth={2} />
                    </div>
                    <div className="stat-value">{formatNumber(stats.customers)}+</div>
                    <div className="stat-label">Happy Customers</div>
                  </div>
                </Col>

                {/* Deliveries Stat */}
                <Col xs={6}>
                  <div className="about-stat-box">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(255, 193, 7, 0.1)' }}>
                      <TrendingUp size={28} color="#ffc107" strokeWidth={2} />
                    </div>
                    <div className="stat-value">{formatNumber(stats.deliveries)}+</div>
                    <div className="stat-label">Deliveries Tracked</div>
                  </div>
                </Col>

                {/* Cities Stat */}
                <Col xs={6}>
                  <div className="about-stat-box">
                    <div className="stat-icon-wrapper" style={{ background: 'rgba(156, 39, 176, 0.1)' }}>
                      <MapPin size={28} color="#9c27b0" strokeWidth={2} />
                    </div>
                    <div className="stat-value">{formatNumber(stats.cities)}+</div>
                    <div className="stat-label">Cities Covered</div>
                  </div>
                </Col>

              </Row>
            </div>
          </Col>

        </Row>
      </Container>
    </section>
  );
};

export default AboutHeroSection;